import { apiClient } from './api-client';
import {
  Property,
  PropertyFormData,
  PropertyFilters,
  ImportUrlRequest,
  PaginatedResponse,
  PropertyAnalysis,
  PropertyNote,
  MarketStats,
} from '@/types/index';

export const propertyService = {
  /**
   * Créer une nouvelle propriété
   */
  create: async (data: PropertyFormData): Promise<Property> => {
    const response = await apiClient.post<Property>('/properties', data);
    return response;
  },

  /**
   * Récupérer toutes les propriétés de l'utilisateur connecté
   */
  getAll: async (): Promise<Property[]> => {
    const response = await apiClient.get<{ properties: Property[] }>('/properties');
    return response.properties;
  },

  /**
   * Récupérer les propriétés avec filtrage (status, isOwned, pagination)
   */
  getAllFiltered: async (
    filters?: PropertyFilters,
  ): Promise<PaginatedResponse<Property>> => {
    const params = new URLSearchParams();

    if (filters?.status) params.append('status', filters.status);
    if (filters?.isOwned !== undefined)
      params.append('isOwned', String(filters.isOwned));
    if (filters?.isOffMarket !== undefined)
      params.append('isOffMarket', String(filters.isOffMarket));
    if (filters?.skip !== undefined) params.append('skip', String(filters.skip));
    if (filters?.take !== undefined) params.append('take', String(filters.take));

    const url = params.toString() ? `/properties?${params}` : '/properties';
    const response =
      await apiClient.get<PaginatedResponse<Property>>(url);
    return response;
  },

  /**
   * Récupérer une propriété par son ID
   */
  getById: async (id: string): Promise<Property> => {
    const response = await apiClient.get<Property>(`/properties/${id}`);
    return response;
  },

  /**
   * Mettre à jour une propriété existante
   */
  update: async (id: string, data: Partial<PropertyFormData>): Promise<Property> => {
    const response = await apiClient.put<Property>(`/properties/${id}`, data);
    return response;
  },

  /**
   * Supprimer une propriété
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/properties/${id}`);
  },

  /**
   * Importer une propriété depuis une URL
   */
  getAnalysis: async (id: string): Promise<PropertyAnalysis> => {
    const response = await apiClient.get<PropertyAnalysis>(`/properties/${id}/analysis`);
    return response;
  },

  estimateSale: async (id: string): Promise<void> => {
    await apiClient.post(`/properties/${id}/estimate-sale`, {});
  },

  checkOffline: async (id: string): Promise<{ available: boolean | null; reason: string; markedWithdrawn: boolean }> => {
    return await apiClient.post(`/properties/${id}/check-offline`, {});
  },

  importFromUrl: async (url: string): Promise<Property> => {
    const response = await apiClient.post<Property>('/properties/import-url', {
      url,
    } as ImportUrlRequest);
    return response;
  },

  // ==================== NOTES ====================

  getNotes: async (propertyId: string): Promise<PropertyNote[]> => {
    const response = await apiClient.get<PropertyNote[]>(`/properties/${propertyId}/notes`);
    return response;
  },

  createNote: async (propertyId: string, content: string): Promise<PropertyNote> => {
    const response = await apiClient.post<PropertyNote>(`/properties/${propertyId}/notes`, { content });
    return response;
  },

  updateNote: async (noteId: string, content: string): Promise<PropertyNote> => {
    const response = await apiClient.patch<PropertyNote>(`/properties/notes/${noteId}`, { content });
    return response;
  },

  deleteNote: async (noteId: string): Promise<void> => {
    await apiClient.delete(`/properties/notes/${noteId}`);
  },

  getMarketStats: async (): Promise<MarketStats> => {
    return apiClient.get<MarketStats>('/properties/market-stats');
  },

  reclassifyAll: async (): Promise<{ total: number; updated: number }> => {
    return apiClient.post('/properties/reclassify-all', {});
  },

  updateSelection: async (
    propertyId: string,
    fields: {
      tauxCapitalisation?: number; downPayment?: number; calculatedRoe?: number;
      flipPrixMarcheM2?: number;
      flipFacteurVente?: number; flipBudgetCuisine?: number; flipBudgetSalleBain?: number;
      flipCoutMenuiserie?: number; flipCoutSol?: number; flipCoutElectricite?: number;
      flipMargeVoulue?: number; flipFraisAchat?: number; flipFraisRevente?: number; flipFraisEmprunt?: number;
    },
  ): Promise<void> => {
    return apiClient.patch(`/properties/${propertyId}/selection`, fields);
  },

  getCounts: async (): Promise<Record<string, number>> => {
    return apiClient.get('/properties/counts');
  },
};
